
export interface ContentImage {
  src: string;
  alt: string;
}

export interface ContentButton {
  text: string;
  link: string;
}

export interface ContentSection {
  id: string;
  title: string;
  icon: string;
  backgroundColor?: string;
  textColor?: string;
  content: string[];
  image?: ContentImage;
  button?: ContentButton;
  buttons?: ContentButton[];
}

export interface NewsArticle {
  image: string;
  title: string;
  description: string;
  link: string;
  linkText: string;
}

export interface AboutContent {
  hero: {
    title: string;
    subtitle: string;
    description: string;
    backgroundVideo?: {
      src: string;
      type: string;
    };
    backgroundImage?: {
      src: string;
      alt: string;
    };
    primaryButton: ContentButton;
    secondaryButton: ContentButton;
  };
  sections: ContentSection[];
  news: {
    title: string;
    articles: NewsArticle[];
  };
}

export interface HomeContent {
  hero: {
    backgroundImage: {
      src: string;
      alt: string;
    };
    title: string;
    subtitle: string;
    button: ContentButton;
  };
  communityIdeas: {
    title: string;
    subtitle: string;
  };
}

export interface SubmitContent {
  header: {
    title: string;
    subtitle: string;
  };
  projectInfo: {
    title: string;
    description: string;
    lookingFor: {
      title: string;
      description: string;
    };
    impact: {
      title: string;
      description: string;
    };
  };
}

class ContentService {
  private aboutCache: Map<string, AboutContent> = new Map();
  private homeCache: Map<string, HomeContent> = new Map();
  private submitCache: Map<string, SubmitContent> = new Map();

  async getAboutContent(tenantId?: string): Promise<AboutContent> {
    // If no tenantId provided, default to severance for now
    // TODO: Get tenantId from tenant config API when implemented
    const resolvedTenantId = tenantId || 'severance';

    // In development mode, skip cache to allow live content updates
    const isDevelopment = process.env.NODE_ENV === 'development';
    
    // Check cache first (skip in development)
    if (!isDevelopment && this.aboutCache.has(resolvedTenantId)) {
      return this.aboutCache.get(resolvedTenantId)!;
    }

    try {
      // Load tenant-specific content
      const response = await fetch(`/content/${resolvedTenantId}/about.json`);
      if (!response.ok) {
        throw new Error(`Failed to load content for tenant ${resolvedTenantId}`);
      }
      
      const content = await response.json() as AboutContent;
      
      // Cache the content
      this.aboutCache.set(resolvedTenantId, content);
      
      return content;
    } catch (error) {
      console.error(`Error loading content for tenant ${resolvedTenantId}:`, error);
      
      // Fallback to severance content if available
      if (resolvedTenantId !== 'severance') {
        console.warn('Falling back to severance content');
        return this.getAboutContent('severance');
      }
      
      // If we can't load severance content either, return minimal content
      return this.getFallbackContent();
    }
  }

  private getFallbackContent(): AboutContent {
    return {
      hero: {
        title: "Community Project",
        subtitle: "Coming Soon",
        description: "Content is being prepared for this project.",
        primaryButton: {
          text: "Share Your Ideas",
          link: "/home"
        },
        secondaryButton: {
          text: "Learn More",
          link: "#"
        }
      },
      sections: [
        {
          id: "fallback",
          title: "Content Loading",
          icon: "Info",
          content: ["Content is being prepared for this community engagement project."]
        }
      ],
      news: {
        title: "Updates",
        articles: []
      }
    };
  }

  private getFallbackHomeContent(): HomeContent {
    return {
      hero: {
        backgroundImage: {
          src: '/images/severance-aerial-old.jpg',
          alt: 'Community space'
        },
        title: 'Shape the Future',
        subtitle: 'Share your ideas and help transform our community space',
        button: {
          text: 'Submit Your Idea',
          link: '/submit'
        }
      },
      communityIdeas: {
        title: 'Community Ideas',
        subtitle: 'Browse and vote on ideas for community development'
      }
    };
  }

  private getFallbackSubmitContent(): SubmitContent {
    return {
      header: {
        title: 'Submit Your Idea',
        subtitle: 'Share your vision for the future of our community space'
      },
      projectInfo: {
        title: 'About This Project',
        description: 'A community-driven initiative to transform our space into something extraordinary.',
        lookingFor: {
          title: 'What We\'re Looking For',
          description: 'Creative ideas for public spaces and community-focused features'
        },
        impact: {
          title: 'Your Impact',
          description: 'Community input is essential to creating a space that reflects local needs'
        }
      }
    };
  }

  async getHomeContent(tenantId?: string): Promise<HomeContent> {
    const resolvedTenantId = tenantId || 'severance';

    // In development mode, skip cache to allow live content updates
    const isDevelopment = process.env.NODE_ENV === 'development';

    // Check cache first (skip in development)
    if (!isDevelopment && this.homeCache.has(resolvedTenantId)) {
      return this.homeCache.get(resolvedTenantId)!;
    }

    try {
      const response = await fetch(`/content/${resolvedTenantId}/home.json`);
      if (!response.ok) {
        throw new Error(`Failed to load home content for tenant ${resolvedTenantId}`);
      }

      const content = await response.json() as HomeContent;
      this.homeCache.set(resolvedTenantId, content);
      return content;
    } catch (error) {
      console.error(`Error loading home content for tenant ${resolvedTenantId}:`, error);
      if (resolvedTenantId !== 'severance') {
        return this.getHomeContent('severance');
      }
      return this.getFallbackHomeContent();
    }
  }

  async getSubmitContent(tenantId?: string): Promise<SubmitContent> {
    const resolvedTenantId = tenantId || 'severance';

    // In development mode, skip cache to allow live content updates
    const isDevelopment = process.env.NODE_ENV === 'development';

    // Check cache first (skip in development)
    if (!isDevelopment && this.submitCache.has(resolvedTenantId)) {
      return this.submitCache.get(resolvedTenantId)!;
    }

    try {
      const response = await fetch(`/content/${resolvedTenantId}/submit.json`);
      if (!response.ok) {
        throw new Error(`Failed to load submit content for tenant ${resolvedTenantId}`);
      }

      const content = await response.json() as SubmitContent;
      this.submitCache.set(resolvedTenantId, content);
      return content;
    } catch (error) {
      console.error(`Error loading submit content for tenant ${resolvedTenantId}:`, error);
      if (resolvedTenantId !== 'severance') {
        return this.getSubmitContent('severance');
      }
      return this.getFallbackSubmitContent();
    }
  }

  clearCache(): void {
    this.aboutCache.clear();
    this.homeCache.clear();
    this.submitCache.clear();
  }

  // Preload content for better performance
  async preloadContent(tenantIds: string[]): Promise<void> {
    const aboutPromises = tenantIds.map(tenantId => 
      this.getAboutContent(tenantId).catch(error => 
        console.warn(`Failed to preload about content for ${tenantId}:`, error)
      )
    );
    
    const homePromises = tenantIds.map(tenantId => 
      this.getHomeContent(tenantId).catch(error => 
        console.warn(`Failed to preload home content for ${tenantId}:`, error)
      )
    );
    
    const submitPromises = tenantIds.map(tenantId => 
      this.getSubmitContent(tenantId).catch(error => 
        console.warn(`Failed to preload submit content for ${tenantId}:`, error)
      )
    );
    
    await Promise.allSettled([...aboutPromises, ...homePromises, ...submitPromises]);
  }
}

export const contentService = new ContentService(); 